/**
 * Settings Overlay Module
 * Handles settings panel collapse/expand, accordion sections, and mobile behavior
 */

// Settings state
let isCollapsed = false;
let isMobile = window.innerWidth <= 768;

/**
 * Initialize settings overlay
 */
export function initSettingsOverlay() {
  setupAccordions();
  setupMobileDetection();
  setupSettingsTabHandlers();

  console.log('[SettingsOverlay] Initialized');
}

/**
 * Setup accordion section behavior
 */
function setupAccordions() {
  const accordionHeaders = document.querySelectorAll('.accordion-header');

  accordionHeaders.forEach(header => {
    header.addEventListener('click', () => {
      // Support both .accordion-item and .accordion-section class names
      const accordionItem = header.closest('.accordion-item') || header.closest('.accordion-section');
      if (!accordionItem) return;

      const content = accordionItem.querySelector('.accordion-content');
      if (!content) return;

      // Toggle expanded state (support both 'expanded' and 'open' classes)
      const isExpanded = accordionItem.classList.contains('expanded') || accordionItem.classList.contains('open');

      if (isExpanded) {
        accordionItem.classList.remove('expanded', 'open');
        content.style.maxHeight = '0';
      } else {
        accordionItem.classList.add('expanded', 'open');
        // Clear inline style to let CSS max-height: 2000px take effect
        // This fixes issues where scrollHeight is incorrect due to hidden content
        content.style.maxHeight = '';
      }
    });
  });

  // Open first accordion by default
  const firstAccordion = document.querySelector('.accordion-item') || document.querySelector('.accordion-section');
  if (firstAccordion) {
    firstAccordion.classList.add('expanded', 'open');
    // No inline style needed - CSS max-height: 2000px handles it
  }
}

/**
 * Setup mobile detection and responsive behavior
 */
function setupMobileDetection() {
  const checkMobile = () => {
    const wasMobile = isMobile;
    isMobile = window.innerWidth <= 768;

    // Close settings overlay on transition to mobile if open
    if (!wasMobile && isMobile) {
      const settingsOverlay = document.getElementById('settingsOverlay');
      if (settingsOverlay?.classList.contains('open')) {
        settingsOverlay.classList.remove('open');
        const settingsBtn = document.querySelector('.icon-btn[data-panel="settings"]');
        if (settingsBtn) settingsBtn.classList.remove('active');
      }
    }
  };

  window.addEventListener('resize', checkMobile);
}

/**
 * Setup settings tab handlers for mobile bottom navigation
 */
function setupSettingsTabHandlers() {
  const tabs = document.querySelectorAll('.settings-tab');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const targetSection = tab.dataset.section;

      // Update active tab
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Scroll to section in settings overlay
      const section = document.querySelector(`.accordion-item[data-section="${targetSection}"]`);
      if (section) {
        // Expand the target accordion
        const wasExpanded = section.classList.contains('expanded');
        if (!wasExpanded) {
          section.querySelector('.accordion-header').click();
        }

        // Scroll into view
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });
}

/**
 * Collapse settings overlay
 */
export function collapseSettings() {
  const settingsOverlay = document.getElementById('settingsOverlay');
  if (settingsOverlay) {
    settingsOverlay.classList.remove('open');
    settingsOverlay.classList.add('collapsed');
    isCollapsed = true;

    const settingsBtn = document.querySelector('.icon-btn[data-panel="settings"]');
    if (settingsBtn) settingsBtn.classList.remove('active');

    document.dispatchEvent(new CustomEvent('settingsCollapsed'));
  }
}

/**
 * Expand settings overlay
 */
export function expandSettings() {
  const settingsOverlay = document.getElementById('settingsOverlay');
  if (settingsOverlay) {
    settingsOverlay.classList.remove('collapsed');
    settingsOverlay.classList.add('open');
    isCollapsed = false;

    const settingsBtn = document.querySelector('.icon-btn[data-panel="settings"]');
    if (settingsBtn) settingsBtn.classList.add('active');

    document.dispatchEvent(new CustomEvent('settingsExpanded'));
  }
}

/**
 * Toggle settings collapsed state
 */
export function toggleSettingsCollapse() {
  if (isCollapsed) {
    expandSettings();
  } else {
    collapseSettings();
  }
}

/**
 * Check if settings is collapsed
 */
export function isSettingsCollapsed() {
  return isCollapsed;
}

/**
 * Open a specific accordion section by name
 */
export function openAccordionSection(sectionName) {
  const section = document.querySelector(`.accordion-item[data-section="${sectionName}"]`);
  if (section && !section.classList.contains('expanded')) {
    section.querySelector('.accordion-header').click();
  }
}

/**
 * Close all accordion sections
 */
export function closeAllAccordions() {
  const expandedSections = document.querySelectorAll('.accordion-item.expanded, .accordion-section.expanded, .accordion-item.open, .accordion-section.open');
  expandedSections.forEach(section => {
    section.classList.remove('expanded', 'open');
    const content = section.querySelector('.accordion-content');
    if (content) content.style.maxHeight = '0';
  });
}
